import PropTypes from 'prop-types'
import { useRef, useState } from 'react'
import { useIntl } from 'react-intl'

import styled from '@emotion/styled'
import {
  Button,
  Flex,
  FlexItem,
  Grid,
  GridCell,
  IconArrow2Left,
  IconArrow2Right,
  Link,
  LINK_VARIANT_HIGHLIGHT,
  SPACING_16,
  SPACING_24,
  SpacingVertical,
  Text,
  TEXT_VARIANT_BOLD
} from 'mailup-web-components'
import { A11y, Navigation, Pagination, Scrollbar } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
// import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import { Slide } from './Slide'

import { useRecoilState } from 'recoil'
import { carouselState } from './recoil/carousel'

const StyledButtonPrev = styled(Button)`
  height: 100% !important;
  background-color: var(--pure-white) !important;
  border-radius: 0 !important;
  border-left: 1px solid var(--geyser) !important;
  border-bottom: 1px solid var(--geyser) !important;
`

const StyledButtonNext = styled(Button)`
  height: 100% !important;
  background-color: var(--pure-white) !important;
  border-radius: 0 !important;
  border-left: 1px solid var(--geyser) !important;
`

const StyledIconArrow2Right = styled(IconArrow2Right)`
  color: ${({ disabled }) =>
    disabled ? 'var(--silver-chalice)' : 'var(--mine-shaft)'} !important;
  font-size: 1.5rem;
`

const StyledIconArrow2Left = styled(IconArrow2Left)`
  color: ${({ disabled }) =>
    disabled ? 'var(--silver-chalice)' : 'var(--mine-shaft)'} !important;
  font-size: 1.5rem;
`

const StyledSwiper = styled(Swiper)`
  .swiper-slide {
    flex: 1;
  }

  .swiper-horizontal > .swiper-scrollbar,
  .swiper-scrollbar.swiper-scrollbar-horizontal {
    left: 0;
    width: 100%;
  }
`

const LeftSide = ({ getTemplateCallback }) => {
  const intl = useIntl()
  const [{ withImage }, setCarouselState] = useRecoilState(carouselState)

  return (
    <FlexItem>
      <Flex flexDirection='column' width='250px'>
        <SpacingVertical variant={SPACING_24} />
        <FlexItem margin='0px 0 0 24px'>
          {withImage && (
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage({
                id: 'Load.ContentsCampaignWizard.TemplateConImmagini'
              })}
            </Text>
          )}
          {!withImage && (
            <Link
              variant={LINK_VARIANT_HIGHLIGHT}
              onClick={() => {
                getTemplateCallback(true).then((e) =>
                  setCarouselState((prev) => ({
                    ...prev,
                    withImage: true,
                    selectedSlide: 0
                  }))
                )
              }}
            >
              {intl.formatMessage({
                id: 'Load.ContentsCampaignWizard.TemplateConImmagini'
              })}
            </Link>
          )}
        </FlexItem>
        <SpacingVertical variant={SPACING_16} />
        <FlexItem margin='0 0 0 24px'>
          {withImage && (
            <Link
              variant={LINK_VARIANT_HIGHLIGHT}
              onClick={() => {
                getTemplateCallback(false).then((e) =>
                  setCarouselState((prev) => ({
                    ...prev,
                    withImage: false,
                    selectedSlide: 0
                  }))
                )
              }}
            >
              {intl.formatMessage({
                id: 'Load.ContentsCampaignWizard.TemplateSenzaImmagini'
              })}
            </Link>
          )}
          {!withImage && (
            <Text variant={TEXT_VARIANT_BOLD}>
              {intl.formatMessage({
                id: 'Load.ContentsCampaignWizard.TemplateSenzaImmagini'
              })}
            </Text>
          )}
        </FlexItem>
      </Flex>
    </FlexItem>
  )
}

const RightSide = ({ swiperRef, isPrevDisabled, isNextDisabled }) => {
  return (
    <Flex flexDirection='column' height='100%'>
      <FlexItem height='50%'>
        <StyledButtonPrev
          onClick={() => swiperRef.current.slidePrev()}
          disabled={isPrevDisabled}
        >
          <StyledIconArrow2Left disabled={isPrevDisabled} />
        </StyledButtonPrev>
      </FlexItem>
      <FlexItem height='50%'>
        <StyledButtonNext
          onClick={() => swiperRef.current.slideNext()}
          disabled={isNextDisabled}
        >
          <StyledIconArrow2Right disabled={isNextDisabled} />
        </StyledButtonNext>
      </FlexItem>
    </Flex>
  )
}
const Carousel = ({ items, getTemplateCallback }) => {
  const swiperRef = useRef()
  const [isPrevDisabled, setPrevDisabled] = useState(true)
  const [isNextDisabled, setNextDisabled] = useState(false)

  const onSlideChange = (swiper) => {
    setPrevDisabled(swiper.isBeginning)
    setNextDisabled(swiper.isEnd)
  }

  const onInit = (swiper) => {
    swiperRef.current = swiper
    setPrevDisabled(swiper.isBeginning)
    setNextDisabled(swiper.isEnd)
  }

  return (
    <Flex height='100%'>
      <Grid cols={12}>
        <GridCell span={2}>
          <LeftSide getTemplateCallback={getTemplateCallback} />
        </GridCell>
        <GridCell span={10}>
          <Flex height='100%'>
            <StyledSwiper
              modules={[Navigation, Pagination, Scrollbar, A11y]}
              slidesPerView='auto'
              slidesPerGroup={1}
              navigation={false}
              scrollbar={{ draggable: true }}
              onBeforeInit={onInit}
              onSlideChange={onSlideChange}
              onScrollbarDragMove={onSlideChange}
              resizeObserver
            >
              {items.map((template, i) => (
                <SwiperSlide key={i}>
                  <Slide
                    index={i}
                    text={template.text}
                    imgSrc={template.imgSrc}
                    isFirst={i === 0}
                  />
                </SwiperSlide>
              ))}
            </StyledSwiper>
            <FlexItem height='100%'>
              <RightSide
                swiperRef={swiperRef}
                isPrevDisabled={isPrevDisabled}
                isNextDisabled={isNextDisabled}
              />
            </FlexItem>
          </Flex>
        </GridCell>
      </Grid>
    </Flex>
  )
}

export default Carousel

Carousel.propTypes = {
  items: PropTypes.array,
  getTemplateCallback: PropTypes.func
}

RightSide.propTypes = {
  swiperRef: PropTypes.any,
  isPrevDisabled: PropTypes.bool,
  isNextDisabled: PropTypes.bool
}

LeftSide.propTypes = {
  getTemplateCallback: PropTypes.func
}
